/**
 * Pallet Management — orchestrator page. Tabs:
 *   • Balances        — live SOH per type / warehouse / merchant
 *   • Movements       — IN/OUT/transfer/adjustment/loss/damage log + truck exchange
 *   • Reconciliation  — match CHEP/Loscam statements vs system stock
 *   • Types           — admin-managed pallet types (Wooden, CHEP, Loscam by default)
 *
 * Aligns with the existing 3PL Billing page architecture (tab strip + KPI tiles).
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import {
  Cube, ArrowsClockwise, ScanSmiley, ListBullets, Stack, Warning, Pulse, MagnifyingGlass,
  TrendUp, ShieldWarning,
} from '@phosphor-icons/react';
import {
  seedPalletDefaults, listPalletTypes, getPalletSummary, getPalletRoiKpis, getPalletRoiSparkline,
  getPalletLossesAvoidedYtd,
} from '../lib/api';
import TypesTab from '../components/pallets/TypesTab';
import MovementsTab from '../components/pallets/MovementsTab';
import BalancesTab from '../components/pallets/BalancesTab';
import ReconciliationTab from '../components/pallets/ReconciliationTab';
import PoolerSyncTab from '../components/pallets/PoolerSyncTab';
import LossInvestigatorTab from '../components/pallets/LossInvestigatorTab';
import LossesAvoidedHero from '../components/pallets/LossesAvoidedHero';

const TABS = [
  { key: 'balances', labelKey: 'pallets.tabs.balances', icon: Stack },
  { key: 'movements', labelKey: 'pallets.tabs.movements', icon: ListBullets },
  { key: 'investigator', labelKey: 'pallets.tabs.investigator', icon: MagnifyingGlass },
  { key: 'reconciliation', labelKey: 'pallets.tabs.reconciliation', icon: ScanSmiley },
  { key: 'pooler', labelKey: 'pallets.tabs.pooler', icon: ArrowsClockwise },
  { key: 'types', labelKey: 'pallets.tabs.types', icon: Cube },
];

function RoiSparkline({ series, accent = '#059669', testId }) {
  if (!series || series.length < 2) {
    return <span className="text-[10px] text-muted-foreground font-mono">no history yet</span>;
  }
  const width = 120;
  const height = 28;
  const values = series.map((s) => s.value_aud || 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(1, max - min);
  const step = width / (series.length - 1);
  const yFor = (v) => height - 2 - ((v - min) / range) * (height - 4);
  const path = values
    .map((v, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${yFor(v).toFixed(1)}`)
    .join(' ');
  const last = values[values.length - 1];
  const first = values[0];
  // Green if the tile's own direction is "up = good" and the trend is up.
  // The tile passes an `accent` colour that already encodes direction so we
  // just render one hue.
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className="inline-block align-middle"
      data-testid={testId}
      role="img"
      aria-label={`6-month trend, latest A$${last.toLocaleString()}`}
    >
      <path
        d={path}
        fill="none"
        stroke={accent}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={(width - 1).toFixed(1)} cy={yFor(last).toFixed(1)} r="2.5" fill={accent} />
      <circle cx="1" cy={yFor(first).toFixed(1)} r="1.5" fill={accent} opacity="0.5" />
    </svg>
  );
}

function KpiTile({ icon: Icon, label, value, sub, color = 'sky', testId, sparkline, sparklineTestId }) {
  const colorMap = {
    sky: { wrap: 'text-sky-700 bg-sky-50 border-sky-200', stroke: '#0284c7' },
    emerald: { wrap: 'text-emerald-700 bg-emerald-50 border-emerald-200', stroke: '#059669' },
    amber: { wrap: 'text-amber-700 bg-amber-50 border-amber-200', stroke: '#d97706' },
    rose: { wrap: 'text-rose-700 bg-rose-50 border-rose-200', stroke: '#e11d48' },
    violet: { wrap: 'text-violet-700 bg-violet-50 border-violet-200', stroke: '#7c3aed' },
  };
  const c = colorMap[color] || colorMap.sky;
  return (
    <div data-testid={testId} className={`border ${c.wrap} p-4 flex flex-col gap-1`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold opacity-80">
          <Icon size={14} weight="bold" />
          {label}
        </div>
        {sparkline && <RoiSparkline series={sparkline} accent={c.stroke} testId={sparklineTestId} />}
      </div>
      <div className="text-2xl font-bold font-mono">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function PalletManagement() {
  const { t } = useTranslation();
  const location = useLocation();
  const initialTab = (() => {
    const p = new URLSearchParams(location.search);
    // iter230 — deep-link from the Competition Demo reconcile flow lands on
    // the Movements tab so the newly-booked adjustment is directly visible.
    if (p.get('highlight')) return 'movements';
    return p.get('tab') || 'balances';
  })();
  const [tab, setTab] = useState(initialTab);
  const [types, setTypes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [roi, setRoi] = useState(null);
  const [roiTrend, setRoiTrend] = useState(null);
  const [ytd, setYtd] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshAll = async () => {
    try {
      const [t, s, r, spark, y] = await Promise.all([
        listPalletTypes().catch(() => ({ data: [] })),
        getPalletSummary().catch(() => ({ data: null })),
        getPalletRoiKpis().catch(() => ({ data: null })),
        getPalletRoiSparkline(6).catch(() => ({ data: null })),
        getPalletLossesAvoidedYtd().catch(() => ({ data: null })),
      ]);
      setTypes(t.data || []);
      setSummary(s.data);
      setRoi(r.data);
      setRoiTrend(spark.data);
      setYtd(y.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load pallet data');
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Idempotent — first load on a fresh tenant seeds the three default types.
        await seedPalletDefaults().catch(() => null);
        await refreshAll();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const activeTypeCount = summary?.active_types ?? '—';
  const palletByType = summary?.by_type || [];
  const wooden = palletByType.find((b) => b.type_code === 'WOOD')?.balance ?? 0;
  const chep = palletByType.find((b) => b.type_code === 'CHEP')?.balance ?? 0;
  const loscam = palletByType.find((b) => b.type_code === 'LOSCAM')?.balance ?? 0;

  return (
    <Layout>
      <div className="space-y-4 p-4 md:p-6 max-w-7xl mx-auto" data-testid="pallet-management-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{t('pallets.page.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('pallets.page.subtitle')}
            </p>
          </div>
        </div>

        <LossesAvoidedHero data={ytd} />

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="pallet-kpis">
          <KpiTile icon={Cube} label={t('pallets.kpis.wooden')} value={wooden.toLocaleString()} sub={t('pallets.kpis.owned')} color="amber" testId="kpi-pallets-wood" />
          <KpiTile icon={Cube} label={t('pallets.kpis.chep')} value={chep.toLocaleString()} sub={t('pallets.kpis.pooled_daily_hire')} color="sky" testId="kpi-pallets-chep" />
          <KpiTile icon={Cube} label={t('pallets.kpis.loscam')} value={loscam.toLocaleString()} sub={t('pallets.kpis.pooled_daily_hire')} color="rose" testId="kpi-pallets-loscam" />
          <KpiTile icon={Pulse} label={t('pallets.kpis.movements_week')} value={summary?.movements_week ?? '—'} sub={`${summary?.loss_damage_week ?? 0} ${t('pallets.kpis.loss_damage_suffix')}`} color="violet" testId="kpi-pallets-movements" />
          <KpiTile icon={Warning} label={t('pallets.kpis.pooled_outstanding')} value={summary?.pooled_balance ?? '—'} sub={`${activeTypeCount} ${t('pallets.kpis.active_types_suffix')}`} color="emerald" testId="kpi-pallets-pooled" />
        </div>

        {/* ROI KPIs — visible for every tier, but especially valuable for the
            $99 Pallet Tracker tier where this widget is the primary ROI signal. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pallet-roi-kpis">
          <KpiTile
            icon={TrendUp}
            label={t('pallets.kpis.recovered_this_month')}
            value={roi ? `A$${(roi.recovered_this_month?.value_aud || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : '—'}
            sub={roi ? `${roi.recovered_this_month?.count_pallets || 0} ${t('pallets.kpis.recovered_sub')}` : t('common.loading')}
            color="emerald"
            testId="kpi-pallets-recovered"
            sparkline={roiTrend?.recovered}
            sparklineTestId="kpi-pallets-recovered-sparkline"
          />
          <KpiTile
            icon={ShieldWarning}
            label={t('pallets.kpis.disputes_this_month')}
            value={roi ? `A$${(roi.disputes_filed_this_month?.value_aud || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : '—'}
            sub={roi ? `${roi.disputes_filed_this_month?.count_cases || 0} ${t('pallets.kpis.disputes_sub_cases')} · ${roi.disputes_filed_this_month?.count_pallets || 0} ${t('pallets.kpis.disputes_sub_pallets')}` : t('common.loading')}
            color="rose"
            testId="kpi-pallets-disputes"
            sparkline={roiTrend?.disputes}
            sparklineTestId="kpi-pallets-disputes-sparkline"
          />
        </div>

        <div className="flex border-b border-border overflow-x-auto" data-testid="pallet-tabs">
          {TABS.map((tab_) => (
            <button
              key={tab_.key}
              type="button"
              onClick={() => setTab(tab_.key)}
              data-testid={`pallet-tab-${tab_.key}`}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
                tab === tab_.key ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab_.icon size={16} />
              {t(tab_.labelKey)}
            </button>
          ))}
        </div>

        {loading && <div className="p-6 text-center text-muted-foreground">Loading…</div>}
        {!loading && (
          <div className="pt-2">
            {tab === 'balances' && <BalancesTab types={types} />}
            {tab === 'movements' && <MovementsTab types={types} onRefresh={refreshAll} />}
            {tab === 'investigator' && <LossInvestigatorTab types={types} />}
            {tab === 'reconciliation' && <ReconciliationTab types={types} />}
            {tab === 'pooler' && <PoolerSyncTab />}
            {tab === 'types' && <TypesTab types={types} onRefresh={refreshAll} />}
          </div>
        )}
      </div>
    </Layout>
  );
}
